import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Message } from '../types';

interface ChatSearchBarProps {
    messages: Message[];
    isDarkMode: boolean;
    onResultSelect: (messageId: string) => void;
}

const ChatSearchBar: React.FC<ChatSearchBarProps> = ({
    messages,
    isDarkMode,
    onResultSelect
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [results, setResults] = useState<Message[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    // Search messages
    useEffect(() => {
        if (searchQuery.trim()) {
            const filtered = messages.filter(msg =>
                msg.content.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setResults(filtered);
            setCurrentIndex(0);
        } else {
            setResults([]);
            setCurrentIndex(0);
        }
    }, [searchQuery, messages]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl/Cmd + F to open search
            if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                e.preventDefault();
                setIsOpen(true);
                setTimeout(() => inputRef.current?.focus(), 100);
            }
            // ESC to close
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    // Navigate to current result
    useEffect(() => {
        if (results.length > 0 && currentIndex < results.length) {
            onResultSelect(results[currentIndex].id);
        }
    }, [currentIndex, results, onResultSelect]);

    const handleNext = () => {
        if (results.length > 0) {
            setCurrentIndex((prev) => (prev + 1) % results.length);
        }
    };

    const handlePrevious = () => {
        if (results.length > 0) {
            setCurrentIndex((prev) => (prev - 1 + results.length) % results.length);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setSearchQuery('');
        setResults([]);
        setCurrentIndex(0);
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => {
                    setIsOpen(true);
                    setTimeout(() => inputRef.current?.focus(), 100);
                }}
                className={`
                    flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-200
                    ${isDarkMode
                        ? 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white border border-slate-700'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200'}
                `}
                title="Search messages (Ctrl+F)"
            >
                <Search size={18} />
                <span className="text-sm font-medium hidden sm:inline">Search</span>
            </button>
        );
    }

    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border shadow-lg animate-in slide-in-from-top-2 ${isDarkMode
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-slate-200'
            }`}>
            <Search size={16} className={isDarkMode ? 'text-slate-500' : 'text-slate-400'} />

            <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search messages..."
                className={`flex-1 bg-transparent border-none outline-none text-sm ${isDarkMode ? 'text-white placeholder-slate-500' : 'text-slate-900 placeholder-slate-400'
                    }`}
            />

            {results.length > 0 && (
                <>
                    <span className={`text-xs font-medium whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                        {currentIndex + 1} / {results.length}
                    </span>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={handlePrevious}
                            className={`p-1 rounded transition-all ${isDarkMode
                                    ? 'hover:bg-slate-700 text-slate-400 hover:text-white'
                                    : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
                                }`}
                            title="Previous (Shift+Enter)"
                        >
                            <ChevronUp size={16} />
                        </button>
                        <button
                            onClick={handleNext}
                            className={`p-1 rounded transition-all ${isDarkMode
                                    ? 'hover:bg-slate-700 text-slate-400 hover:text-white'
                                    : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
                                }`}
                            title="Next (Enter)"
                        >
                            <ChevronDown size={16} />
                        </button>
                    </div>
                </>
            )}

            <button
                onClick={handleClose}
                className={`p-1 rounded transition-all ${isDarkMode
                        ? 'hover:bg-slate-700 text-slate-400 hover:text-white'
                        : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
                    }`}
                title="Close (Esc)"
            >
                <X size={16} />
            </button>
        </div>
    );
};

export default ChatSearchBar;
